import { useQueryClient } from "@tanstack/react-query";
import { RUNNING_BUILD } from "@version/deployedBuild";
import { useEffect } from "react";

import { getConfig } from "../config";
import { displaysKeys } from "./displaysQueries";

/** brian's display push route; authenticated by the display cookie, frames are `{"type": "refresh"}`. */
const DISPLAY_SOCKET_PATH = "/ws/display";

const BASE_BACKOFF_MS = 1_000;
const MAX_BACKOFF_MS = 30_000;

const isRefresh = (raw: unknown): boolean => {
  if (typeof raw !== "string") return false;
  try {
    const frame: unknown = JSON.parse(raw);
    return (
      typeof frame === "object" &&
      frame !== null &&
      (frame as { type?: unknown }).type === "refresh"
    );
  } catch {
    return false;
  }
};

/** Re-reads the screen's view on every push and on every (re)connect, since missed frames are never replayed. */
export const useDisplayViewSocket = () => {
  const queryClient = useQueryClient();

  useEffect(() => {
    // brian logs the build so screens left on an old one show up.
    const build = RUNNING_BUILD
      ? `?build=${encodeURIComponent(RUNNING_BUILD)}`
      : "";
    const url = `${getConfig().wsUrl}${DISPLAY_SOCKET_PATH}${build}`;
    const refresh = () =>
      void queryClient.invalidateQueries({ queryKey: displaysKeys.view });

    let ws: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let attempt = 0;
    let disposed = false;

    const scheduleReconnect = () => {
      if (disposed || reconnectTimer) return;
      const delay = Math.min(BASE_BACKOFF_MS * 2 ** attempt, MAX_BACKOFF_MS);
      attempt += 1;
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        connect();
      }, delay);
    };

    function connect() {
      if (disposed) return;
      const socket = new WebSocket(url);
      ws = socket;
      socket.onopen = () => {
        attempt = 0;
        refresh();
      };
      socket.onmessage = (e) => {
        if (isRefresh(e.data)) refresh();
      };
      socket.onerror = () => socket.close();
      socket.onclose = () => {
        if (ws === socket) ws = null;
        scheduleReconnect();
      };
    }

    connect();

    return () => {
      disposed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (ws) {
        ws.onopen = ws.onmessage = ws.onerror = ws.onclose = null;
        ws.close();
      }
    };
  }, [queryClient]);
};
